import React, { FunctionComponent, useEffect, useState } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import "dayjs/locale/de";

dayjs.extend(utc);

import Registration from "../../models/registration/Registration";
import Limit from "../../models/limit/Limit";
import FormSchema from "../../models/schema/FormSchema";
import RegistrationEditor from "./RegistrationEditor";
import { CloseIcon, EditIcon, TransferIcon, TrashIcon } from "../common/sidebar/icons";
import { joyColorClass } from "../common/joyColor";
import { resolveFieldEditor } from "./registrationFieldEditor";

type FormData = Record<string, unknown>;

/** Eigene Id, damit der Drawer nicht mit dem Formular der Editor-Seite kollidiert. */
const DRAWER_FORM_ID = "registration-drawer-form";

interface Props {
  /** Rohdaten der Anmeldung — nicht die für die Tabelle aufbereitete Kopie. */
  registration: Registration;
  properties: string[];
  limits: Limit[];
  formSchema?: FormSchema;
  /** Überschrift des Panels, z.B. für Wartelisteneinträge. */
  title?: string;
  offersLabel?: string;
  onClose: () => void;
  /** Ohne eigene Editor-Seite öffnet "Bearbeiten" die Bearbeitung im Panel. */
  onOpenEditor?: () => void;
  onDelete: () => void;
  onSave: (formData: FormData) => Promise<void>;
  /** Zusätzliche Aktion, z.B. Wartelisteneintrag in die Anmeldungen übernehmen. */
  onTransfer?: () => void;
}

const displayValue = (value: unknown, kind: string): string => {
  if (value === undefined || value === null || value === "") return "—";
  if (kind === "boolean") return value ? "Ja" : "Nein";
  if (kind === "date") {
    const date = dayjs.utc(String(value));
    return date.isValid() ? date.locale("de").format("DD. MMMM YYYY") : String(value);
  }
  return String(value);
};

export const RegistrationDetails: FunctionComponent<Props> = ({
  registration,
  properties,
  limits,
  formSchema,
  title = "Anmeldung",
  offersLabel = "Gewählte Angebote",
  onClose,
  onOpenEditor,
  onDelete,
  onSave,
  onTransfer
}) => {
  const [isEditing, setEditing] = useState(false);
  const [isSaving, setSaving] = useState(false);
  const [focusProperty, setFocusProperty] = useState<string | null>(null);

  // Beim Wechsel des Eintrags zurück in die Leseansicht.
  useEffect(() => {
    setEditing(false);
  }, [registration.id]);

  // Nach dem Aufklappen in das angeklickte Feld springen. rjsf vergibt die Ids
  // als `root_<Feldname>`; Felder mit eigenem Aufbau (z.B. Datum) finden wir
  // nicht — dann bleibt es beim geöffneten Formular ohne Fokus.
  useEffect(() => {
    if (!isEditing || !focusProperty) return;
    const input = document.getElementById(`root_${focusProperty}`);
    if (input) {
      input.scrollIntoView({ block: "nearest" });
      input.focus();
    }
    setFocusProperty(null);
  }, [isEditing, focusProperty]);

  const limitProperties = limits.map((limit) => limit.property);
  const textProperties = properties.filter((property) => !limitProperties.includes(property));
  const chosenOffers = limitProperties.filter((property) => registration.formData[property]);

  const totalPrice = limits
    .map((limit) => (registration.formData[limit.property] && limit.price ? limit.price : 0))
    .reduce((total, current) => total + current, 0);

  const offerPills = (
    <span className="detail-item__tags">
      {chosenOffers.map((offer) => (
        <span key={offer} className={`tag-pill tag-pill${joyColorClass(offer)}`}>
          {offer}
        </span>
      ))}
    </span>
  );

  const save = (formData: FormData): void => {
    setSaving(true);
    onSave(formData)
      .then(() => setEditing(false))
      .finally(() => setSaving(false));
  };

  const canEdit = !!formSchema;

  const startEditing = (property: string): void => {
    if (!canEdit) return;
    setFocusProperty(property);
    setEditing(true);
  };

  return (
    <aside className="panel detail-panel">
      <div className="panel__head panel__head--tinted">
        <h2>{title}</h2>
        <span className="panel__meta">{isEditing ? "Bearbeiten" : "Feld anklicken zum Bearbeiten"}</span>
        <button type="button" title="Schliessen" className="btn btn-icon" onClick={onClose}>
          <CloseIcon />
        </button>
      </div>

      <div className="detail-panel__body">
        {isEditing && formSchema ? (
          <div className="detail-panel__form">
            <RegistrationEditor
              formSchema={formSchema}
              formData={registration.formData}
              isSaving={isSaving}
              onSubmit={save}
              formId={DRAWER_FORM_ID}
              showSubmitButton={false}
            />
          </div>
        ) : (
          <>
            {textProperties.map((property) => {
              const { kind } = resolveFieldEditor(property, formSchema);
              return (
                <div className="detail-item" key={property}>
                  <span className="detail-item__label">{property}</span>
                  {canEdit ? (
                    <button
                      type="button"
                      className="detail-item__value detail-item__value--editable"
                      title="Zum Bearbeiten klicken"
                      onClick={() => startEditing(property)}>
                      {displayValue(registration.formData[property], kind)}
                    </button>
                  ) : (
                    <span className="detail-item__value">{displayValue(registration.formData[property], kind)}</span>
                  )}
                </div>
              );
            })}

            <div className="detail-item">
              <span className="detail-item__label">{offersLabel}</span>
              {canEdit ? (
                <button
                  type="button"
                  className="detail-item__value detail-item__value--editable"
                  title="Zum Bearbeiten klicken"
                  onClick={() => startEditing(limitProperties[0])}>
                  {chosenOffers.length === 0 ? "—" : offerPills}
                </button>
              ) : (
                <span className="detail-item__value">{chosenOffers.length === 0 ? "—" : offerPills}</span>
              )}
            </div>

            <div className="detail-item">
              <span className="detail-item__label">Gesamtpreis</span>
              <span className="detail-item__value">CHF {totalPrice}</span>
            </div>

            <div className="detail-item">
              <span className="detail-item__label">Erstellt</span>
              <span className="detail-item__value">{registration.creation.createdAt.toLocaleString()}</span>
            </div>

            <div className="detail-item">
              <span className="detail-item__label">Zuletzt aktualisiert</span>
              <span className="detail-item__value">{registration.creation.updatedAt.toLocaleString()}</span>
            </div>
          </>
        )}
      </div>

      <div className="panel__bar detail-panel__actions">
        {isEditing ? (
          <>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              disabled={isSaving}
              onClick={() => setEditing(false)}>
              Abbrechen
            </button>
            <button type="submit" form={DRAWER_FORM_ID} className="btn btn-primary btn-sm" disabled={isSaving}>
              {isSaving ? "Speichert …" : "Speichern"}
            </button>
          </>
        ) : (
          <>
            <button type="button" className="btn btn-secondary btn-sm" onClick={onDelete}>
              <TrashIcon size={13} /> Löschen
            </button>
            {onTransfer && (
              <button type="button" className="btn btn-secondary btn-sm" onClick={onTransfer}>
                <TransferIcon size={13} /> Übernehmen
              </button>
            )}
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={!onOpenEditor && !canEdit}
              onClick={() => (onOpenEditor ? onOpenEditor() : setEditing(true))}>
              <EditIcon size={13} /> Bearbeiten
            </button>
          </>
        )}
      </div>
    </aside>
  );
};
